'use client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useBunja } from 'bunja/react';
import { isEmpty, pick } from 'es-toolkit/compat';
import { useAtomValue } from 'jotai';
import { Suspense, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { postUserAuthMfaConfirmApi, postUserLoginApi } from '@flipster/apis/v1/user';
import { SegmentPage } from '@flipster/features/src/misc/segment/segment-page';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { showErrorDialog } from '@flipster/features/src/shared/show-error-dialog';
import { FormattedMessage } from '@flipster/i18n';
import { supportedLocaleToLanguageTag } from '@flipster/i18n/constants';
import { useSupportedLocale } from '@flipster/i18n/hooks';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { useSendApi } from '@flipster/react/misc/use-send-api';
import { checkAxiosError } from '@flipster/utils/axios';
import { getErrorCode, getErrorMessage } from '@flipster/utils/error';
import segment, { SessionEvent } from '@flipster/utils/segment';

import VerifyEmail from '@web/components/VerifyEmail';
import { captchaAtom, useCaptcha } from '@web/hooks/useCaptcha';
import { LoginSchema, loginValidator } from '@web/utils/session-validator';

import { mfaVerifyFlowBunja } from '../../_mfa/bunja';
import { varaTransitionAgreementBunja } from '../../_modal/VaraTransitionAgreementModal/bunja';
import { passkeyLoginFlowBunja } from '../../_passkey/bunja';
import LoginForm from './LoginForm';
import { ninetyDaysPasswordUpdateBunja } from './_90days-password-update';

const RequiredFields = ['email', 'password'] as const;

const Page = () => {
  const loginFormRef = useRef<HTMLFormElement>(null);

  const methods = useForm<LoginSchema>({
    mode: 'onTouched',
    resolver: yupResolver(loginValidator),
    defaultValues: {
      geetestCaptchaResponse: undefined,
    },
  });
  const [step, setStep] = useState<'login' | 'verify'>('login');
  const [loading, setLoading] = useState(false);
  const captcha = useAtomValue(captchaAtom);
  const locale = useSupportedLocale();

  const { getMe } = useAuth();
  const { startVerify } = useBunja(mfaVerifyFlowBunja);
  const { openPasswordChangeRequiredModal } = useBunja(ninetyDaysPasswordUpdateBunja);
  const { openPasskeyLoginModal } = useBunja(passkeyLoginFlowBunja);
  const { checkTncAgreementModalNeedToShow, openTncAgreementModal } = useBunja(varaTransitionAgreementBunja);
  const {
    watch,
    handleSubmit,
    formState: { dirtyFields, errors },
    setValue,
    setError,
  } = methods;

  const [isLoading, submit] = useSendApi(async (formValues: LoginSchema) => {
    const checkLogin = async () => {
      const { data: me } = await getMe();
      if (me) {
        openPasswordChangeRequiredModal(me);
        const isTncAgreementModalNeedToShow = await checkTncAgreementModalNeedToShow(me.userId);

        if (isTncAgreementModalNeedToShow) openTncAgreementModal({ from: 'organic_login' });
      }
    };

    const errorHandler = (error: unknown) => {
      setLoading(false);
      if (!checkAxiosError(error)) return;
      const errorCode = getErrorCode(error);
      switch (errorCode) {
        case 'api.recaptchaResponseTokenRequired':
        case 'api.recaptchaVerification.timeoutOrDuplicate':
        case 'api.captchaResponseRequired':
        case 'api.geetestCaptchaValidation.duplicate':
          setValue('geetestCaptchaResponse', undefined);
          captcha?.showCaptcha();
          break;
        case 'api.user.auth.emailNotVerified':
          setStep('verify');
          break;
        case 'api.authFailureLimitExceed':
          dialog.open({
            title: {
              label: <FormattedMessage id='Autogen_common.r251126.s_Too_many_failed_attempts' />,
            },
            content: (
              <>
                <FormattedMessage id='Autogen_common.r251126.s_Due_to_multiple_failed_login_attempts_your_account_has_been_locked_for_a_2_hour_period' />
                <br />
                <FormattedMessage id='Autogen_common.r251126.s_Please_try_again_later_or_reset_your_password' />
              </>
            ),
            buttons: {
              primary: <Dialog.Button hierarchy='primary' label='Confirm' />,
            },
          });
          break;
        case 'api.authBlocked':
          showErrorDialog(error);
          break;
        case 'api.invalidEmailFormat':
        case 'api.invalidParameter':
        case 'api.user.kycClientStatusClosed':
        case 'api.user.auth.alreadySignedUpUsingSocial':
          setError('email', { type: 'server', message: getErrorMessage(error) });
          break;
        case 'api.user.auth.invalidPassword':
        case 'api.user.auth.loginAndPasswordNotMatching':
          setError('password', { type: 'server', message: getErrorMessage(error) });
          break;
        default:
          showErrorDialog(error);
      }
    };

    try {
      const params = watch('geetestCaptchaResponse') ? formValues : pick(formValues, ['email', 'password']);
      const { data } = await postUserLoginApi({
        ...params,
        lang: supportedLocaleToLanguageTag[locale],
      });
      const { authToken, requireMfa, phoneNumber } = data ?? {};

      if (requireMfa && authToken) {
        startVerify('AUTH', {
          authToken,
          phoneNumber,
          onConfirm: async (mfaSessionToken) => {
            setLoading(true);
            try {
              await postUserAuthMfaConfirmApi({ mfaSessionToken, authToken });
              await checkLogin();
            } catch (error) {
              errorHandler(error);
            }
          },
        });
      } else {
        await checkLogin();
      }

      segment.track(SessionEvent.LogInEmailClick);
    } catch (error) {
      errorHandler(error);
    }
  });

  useCaptcha({
    submitFunc: handleSubmit(submit),
    setGeetestCaptchaResponse: (value) => setValue('geetestCaptchaResponse', value),
  });

  const isValid = isEmpty(errors) && RequiredFields.every((key) => dirtyFields[key]);

  return (
    <>
      <SegmentPage name={SessionEvent.LoginPageView} />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submit)} ref={loginFormRef}>
          {step === 'login' && (
            <Suspense>
              <LoginForm
                loading={isLoading || loading}
                disabled={!isValid}
                onPasskeyButtonClick={openPasskeyLoginModal}
              />
            </Suspense>
          )}
          {step === 'verify' && <VerifyEmail email={watch('email')} />}
        </form>
      </FormProvider>
    </>
  );
};

export default Page;
